// src/components/OrderSignedDeliveryNote.js

import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Spin,
  Image as AntImage,
  Input,
  Select,
  Upload,
  Modal,
  Form,
  List,
  Tooltip,
  Checkbox,
} from "antd";
import {
  UploadOutlined,
  FilePdfOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  UserOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  MailOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import { useOrderContext } from "../contexts/OrderContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const { Option } = Select;

/* ---------- helpers ---------- */
const splitProductName = (text) => {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  const words = clean.split(" ");
  if (words.length <= 2) return clean;
  const mid = Math.ceil(words.length / 2);
  return (
    <>
      {words.slice(0, mid).join(" ")}
      <br />
      {words.slice(mid).join(" ")}
    </>
  );
};

const getCompanyName = (rec) =>
  rec?.company_name ||
  rec?.customer_company ||
  rec?.customerCompany ||
  rec?.user_company ||
  rec?.user?.company_name ||
  rec?.user?.companyName ||
  rec?.customer?.company_name ||
  rec?.customer?.companyName ||
  "-";

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const direct = new Date(value);
  if (!Number.isNaN(+direct)) return direct;
  const m = String(value).match(/^(\d{1,2})\s([A-Za-z]{3,})\s(\d{4})$/);
  if (!m) return null;
  const months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };
  const day = parseInt(m[1], 10);
  const mon = months[m[2].slice(0, 3).toLowerCase()];
  const year = parseInt(m[3], 10);
  if (mon == null) return null;
  return new Date(year, mon, day);
};

const formatShortDate = (value) => {
  const d = parseDate(value);
  if (!d) return String(value ?? "");
  return d.toLocaleDateString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
};

const toDayStart = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/* ---------- component ---------- */
const OrderSignedDeliveryNote = () => {
  const { orders, loading, fetchOrders, updateDeliveryStatus } = useOrderContext();

  const userRole = localStorage.getItem("userRole") || "";
  const isSalesUser = userRole === "sales";

  const [trackingFilter, setTrackingFilter] = useState("");
  const [referenceFilter, setReferenceFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState(null);

  const [selectedDocketIds, setSelectedDocketIds] = useState([]);

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewDocuments, setViewDocuments] = useState([]);

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadModalOrder, setUploadModalOrder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadComment, setUploadComment] = useState("");

  // ✅ NEW bulk upload per customer
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  const [bulkUploadCustomer, setBulkUploadCustomer] = useState(null);
  const [bulkCustomerOrders, setBulkCustomerOrders] = useState([]);
  const [bulkSelectedOrders, setBulkSelectedOrders] = useState([]);
  const [bulkSelectedFile, setBulkSelectedFile] = useState(null);
  const [bulkUploadComment, setBulkUploadComment] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);

  // preload /logo192.png
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.src = `${process.env.PUBLIC_URL || ""}/logo192.png`;
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        setLogoDataUrl(c.toDataURL("image/png"));
      } catch {
        setLogoDataUrl(null);
      }
    };
  }, []);

  const isPDF = (url) => url?.toLowerCase().endsWith(".pdf");

  // ✅ Now fetch documents from backend per order
  const handleViewDocument = async (order) => {
    try {
      const res = await axiosInstance.get(`/order/order-documents/${order.id}`);
      const docs = res.data?.documents || [];
      if (!docs.length) {
        message.info("No document to view.");
        return;
      }
      setViewDocuments(docs);
      setViewModalVisible(true);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch documents.");
    }
  };

  const handleOpenDocument = (url) => window.open(url, "_blank");

  // ✅ make preview helper generic
  const renderFilePreview = (file) => {
    if (!file) return null;
    if (file.type?.startsWith("image/")) {
      return (
        <AntImage
          src={URL.createObjectURL(file)}
          alt="Preview"
          className="max-w-full mt-2"
        />
      );
    }
    if (file.type === "application/pdf") {
      return (
        <div className="text-center mt-2">
          <FilePdfOutlined className="text-4xl text-blue-500" />
          <p className="mt-1">{file.name}</p>
        </div>
      );
    }
    return <p className="mt-2">{file.name}</p>;
  };

  // ✅ single-order upload – now sends company_name
  const handleUploadSubmit = async () => {
    if (!uploadModalOrder || !selectedFile) {
      return message.error("Please select a file.");
    }
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("document", selectedFile);
    formData.append("orderId", uploadModalOrder.id);
    formData.append("company_name", getCompanyName(uploadModalOrder));
    formData.append("comment", uploadComment || "");

    try {
      const res = await axiosInstance.post("/order/upload-document", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data.success) {
        message.success("Uploaded successfully!");
        setUploadModalVisible(false);
        setSelectedFile(null);
        setUploadComment("");
        fetchOrders();
      } else {
        message.error("Upload failed.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error uploading document.");
    }
  };

  // ✅ NEW – open bulk upload modal for currently filtered customer
  const openBulkUploadForCurrentCustomer = () => {
    if (!selectedDocketIds.length) {
      message.warning("Please select at least one order.");
      return;
    }

    const selectedOrders = displayedOrders.filter((o) => selectedDocketIds.includes(o.id));

    const uniqueCompanies = new Set(selectedOrders.map((o) => getCompanyName(o)));

    if (uniqueCompanies.size > 1) {
      message.error("Selected orders belong to different customers.");
      return;
    }

    const companyName = getCompanyName(selectedOrders[0]);

    setBulkUploadCustomer(companyName);
    setBulkCustomerOrders(selectedOrders);
    setBulkSelectedOrders(selectedOrders.map((o) => o.id)); // default select all
    setBulkSelectedFile(null);
    setBulkUploadComment("");
    setBulkUploadVisible(true);
  };

  // ✅ NEW – bulk upload: one file → multiple orders (looping)
  const handleBulkUploadSubmit = async () => {
    if (!bulkUploadCustomer) {
      return message.error("Customer is required.");
    }
    if (!bulkSelectedFile) {
      return message.error("Please select a file.");
    }
    if (!bulkSelectedOrders.length) {
      return message.error("Please select at least one order.");
    }

    setBulkUploading(true);
    const token = localStorage.getItem("token");
    let successCount = 0;
    let failCount = 0;

    try {
      for (const orderId of bulkSelectedOrders) {
        const formData = new FormData();
        formData.append("document", bulkSelectedFile);
        formData.append("orderId", orderId);
        formData.append("company_name", bulkUploadCustomer);
        formData.append("comment", bulkUploadComment || "");

        try {
          const res = await axiosInstance.post("/order/upload-document", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          });
          if (res.data?.success) {
            successCount += 1;
          } else {
            failCount += 1;
          }
        } catch (err) {
          console.error("Bulk upload error for order", orderId, err);
          failCount += 1;
        }
      }

      if (successCount && !failCount) {
        message.success(`Docket uploaded for ${successCount} order(s) of ${bulkUploadCustomer}.`);
      } else if (successCount && failCount) {
        message.warning(`Docket uploaded for ${successCount} order(s), but ${failCount} failed.`);
      } else {
        message.error("Failed to upload docket for all selected orders.");
      }

      setBulkUploadVisible(false);
      setBulkSelectedFile(null);
      setBulkUploadComment("");
      setBulkSelectedOrders([]);
      await fetchOrders();
    } finally {
      setBulkUploading(false);
    }
  };

  const handleMarkAsDelivered = async (record) => {
    try {
      await updateDeliveryStatus(record.id, "order delivered");
      await fetchOrders();
    } catch {
      message.error("Failed to update delivery status.");
    }
  };

  const customerOptions = useMemo(() => {
    const counts = new Map();
    const displayedOrdersSrc = orders.filter(
      (o) => o.deliveryStatus === "scheduled" || o.deliveryStatus === "order delivered"
    );
    displayedOrdersSrc.forEach((o) => {
      const name = getCompanyName(o) || "-";
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [orders]);

  /* filtering */
  let filtered = orders.filter((order) => {
    const t = trackingFilter.trim().toLowerCase();
    const r = referenceFilter.trim().toLowerCase();
    const trackOk =
      !t ||
      String(order.trackingId || order.orderId || "")
        .toLowerCase()
        .includes(t);
    const refOk =
      !r ||
      String(order.referenceNumber || "")
        .toLowerCase()
        .includes(r);
    return trackOk && refOk;
  });

  if (customerFilter) {
    filtered = filtered.filter((o) => (getCompanyName(o) || "-") === customerFilter);
  }

  const displayedOrders = filtered
    .filter((o) => o.deliveryStatus === "scheduled" || o.deliveryStatus === "order delivered")
    .sort(
      (a, b) =>
        new Date(b.orderDate || b.order_date) - new Date(a.orderDate || a.order_date)
    );

  const isOverdue = (rec) => {
    if (rec.deliveryStatus !== "scheduled") return false;
    const d = parseDate(rec.scheduleDate);
    if (!d) return false;
    const today = toDayStart(new Date());
    return toDayStart(d) < today;
  };

  const statusForBoth = (rec) =>
    rec.deliveryStatus === "order delivered" ? "delivered" : "scheduled";

  const rowsForExport = (list) =>
    list.map((o, idx) => {
      const status = statusForBoth(o);
      let when = "";
      if (status === "delivered") {
        when = new Date(o.deliveredAt || "").toLocaleString();
      } else if (status === "scheduled") {
        const d = parseDate(o.scheduleDate);
        when = d
          ? d.toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : String(o.scheduleDate || "");
      }
      return [
        idx + 1,
        getCompanyName(o),
        o.trackingId || o.orderId || "",
        o.referenceNumber || "",
        o.product_name || "",
        o.size || "",
        o.quantity ?? "",
        status,
        when,
        (o.shipping_address || "").replace(/\s+/g, " ").trim(),
      ];
    });

  const getSelectionOrAll = () =>
    selectedDocketIds.length
      ? displayedOrders.filter((o) => selectedDocketIds.includes(o.id))
      : displayedOrders;

  const handleExportPDF = () => {
    // ✅ Sales should NOT export/print/email
    if (isSalesUser) return;

    const source = getSelectionOrAll();
    if (!source.length) return message.info("No orders to export.");

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "A4",
    });
    const title = "Delivery List";
    doc.setFontSize(16);
    doc.text(title, 40, 40);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 58);

    const drawFooter = (docInstance) => {
      try {
        const pageCount = docInstance.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          docInstance.setPage(i);
          const pW = docInstance.internal.pageSize.getWidth();
          const pH = docInstance.internal.pageSize.getHeight();
          docInstance.setFontSize(9);
          docInstance.setTextColor(120);
          docInstance.text(`Page ${i} of ${pageCount}`, pW / 2, pH - 16, {
            align: "center",
          });
          docInstance.setTextColor(0);
        }
      } catch {}
    };

    autoTable(doc, {
      head: [
        [
          "#",
          "Customer",
          "Order ID",
          "Customer Ref",
          "Product",
          "Size",
          "Qty",
          "Status",
          "Date",
          "Address",
        ],
      ],
      body: rowsForExport(source),
      startY: 72,
      tableWidth: "wrap",
      margin: { left: 40, right: 40 },
      styles: {
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
        cellWidth: "wrap",
        valign: "top",
      },
      headStyles: {
        fillColor: [18, 92, 140],
        textColor: 255,
        fontStyle: "bold",
      },
      didDrawPage: () => drawFooter(doc),
    });

    doc.save("Delivery_List.pdf");
  };

  const handleExportExcel = () => {
    // ✅ Sales should NOT export/print/email
    if (isSalesUser) return;

    const source = getSelectionOrAll();
    if (!source.length) return message.info("No orders to export.");

    const headers = [
      "#",
      "Customer",
      "Order ID",
      "Customer Ref",
      "Product",
      "Size",
      "Qty",
      "Status",
      "Date",
      "Address",
    ];
    const rows = rowsForExport(source);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = [
      { wch: 4 },
      { wch: 24 },
      { wch: 16 },
      { wch: 22 },
      { wch: 24 },
      { wch: 10 },
      { wch: 6 },
      { wch: 11 },
      { wch: 14 },
      { wch: 40 },
    ];
    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      }),
    };
    XLSX.utils.book_append_sheet(wb, ws, "Delivery List");
    XLSX.writeFile(wb, "Delivery_List.xlsx");
  };

  /* ===== Layout helpers for PDF ===== */
  const pageFooter = (doc) => {
    try {
      const pageCount = doc.internal.getNumberOfPages();
      const w = doc.internal.pageSize.getWidth();
      const h = doc.internal.pageSize.getHeight();
      const n = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`Page ${n} of ${pageCount}`, w / 2, h - 16, {
        align: "center",
      });
      doc.setTextColor(0);
    } catch {}
  };

  const normalizeCategoryDisplay = (names = []) => {
    const first = (names[0] || "").toLowerCase();
    if (first === "beds") return "Bed";
    if (first === "mattresses") return "Mattress";
    return names[0] || "Other";
  };

  const getCategory = (o) => {
    if (o.productCategoryDisplay) return o.productCategoryDisplay;
    if (Array.isArray(o.product_categories) && o.product_categories.length) {
      return normalizeCategoryDisplay(o.product_categories);
    }
    const n = `${o.product_name || ""}`.toLowerCase();
    if (n.includes("mattress")) return "Mattress";
    if (n.includes("bed")) return "Bed";
    return "Other";
  };

  const normalizeSize = (raw = "") => {
    const s = String(raw).toLowerCase();
    if (s.includes("super king") || s.includes("6ft")) return "Super King";
    if (s.includes("king") || s.includes("5ft")) return "King";
    if (s.includes("small double") || s.includes("queen") || s.includes("4ft"))
      return "Small Double";
    if (s.includes("double") || s.includes("4ft6")) return "Double";
    if (s.includes("small single")) return "Small Single";
    if (s.includes("single") || s.includes("3ft")) return "Single";
    return raw || "-";
  };

  const sizeOrder = {
    "Small Single": 0,
    Single: 1,
    "Small Double": 2,
    Double: 3,
    King: 4,
    "Super King": 5,
    "-": 99,
  };

  const buildGroupedRows = (ordersForCustomer) => {
    const agg = new Map(); // key: cat|size|code|name
    ordersForCustomer.forEach((o) => {
      const cat = getCategory(o);
      const size = normalizeSize(o.size);
      const code = o.code || o.sku || "";
      const name = (o.product_name || "").trim();
      const key = `${cat}|${size}|${code}|${name}`;
      const qty = Number(o.quantity ?? 0) || 0;
      if (!agg.has(key)) {
        agg.set(key, { cat, size, code, name, qty, ref: getCompanyName(o) });
      } else {
        agg.get(key).qty += qty;
      }
    });

    const rows = Array.from(agg.values());
    rows.sort((a, b) => {
      const catRank = (c) => (c === "Bed" ? 0 : c === "Mattress" ? 1 : 2);
      const ca = catRank(a.cat),
        cb = catRank(b.cat);
      if (ca !== cb) return ca - cb;
      const sa = sizeOrder[a.size] ?? 99;
      const sb = sizeOrder[b.size] ?? 99;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name);
    });

    return rows.map((r) => [r.cat, r.size, r.code, `${r.name}\nREF: ${r.ref}`, String(r.qty)]);
  };

  // ====== SINGLE-ORDER Docket ======
  const drawDocket = (doc, order) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const innerW = pageWidth - margin * 2;

    const set = (size, style = "normal") => {
      doc.setFont("helvetica", style);
      doc.setFontSize(size);
    };
    const text = (t, x, y, opts) => doc.text(String(t ?? ""), x, y, opts);

    // Header
    let y = margin;
    const logoTopY = y - 6;
    const logoH = 55;
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, "PNG", margin, logoTopY, 110, logoH);
      } catch {}
    }

    const rightX = pageWidth - margin;
    let topY = y + 6;

    set(16, "bold");
    text("Serena Beds Limited", rightX, topY, { align: "right" });

    set(10, "normal");
    topY += 14;
    text("Unit 2, Ashbourne Business Centre", rightX, topY, { align: "right" });
    topY += 14;
    text("Ashbourne, Meath, A84 R960", rightX, topY, { align: "right" });
    topY += 14;
    text("Ireland", rightX, topY, { align: "right" });

    topY += 8;
    const lh = 12;
    topY += lh;
    text("www.serenabeds.ie", rightX, topY, { align: "right" });
    topY += lh;
    text("T: 08383855666", rightX, topY, { align: "right" });
    topY += lh;
    text("sales@serenabeds.ie", rightX, topY, { align: "right" });

    const headerBottom = Math.max(logoTopY + logoH, topY);

    y = headerBottom + 10;
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);

    // Title + meta
    y += 16;
    set(13, "bold");
    text("DELIVERY NOTE", rightX, y, { align: "right" });

    set(10, "normal");
    const metaY = y + 18;
    text("Date", margin, metaY);
    text(new Date(order.orderDate || order.order_date || Date.now()).toLocaleDateString(), margin + 55, metaY);

    set(9);
    text("Invoice Number", rightX, metaY, { align: "right" });
    set(10);
    text(String(order.trackingId || order.orderId || order.id || "-"), rightX, metaY + 14, { align: "right" });

    // Address table
    y = metaY + 24;
    const invoiceTo =
      (order.billing_address || order.shipping_address || "").trim() || getCompanyName(order);
    const deliverTo =
      (order.shipping_address || "").trim() || getCompanyName(order);

    const addrColW = Math.floor(innerW / 2);
    autoTable(doc, {
      startY: y,
      head: [["Invoice To", "Deliver To"]],
      body: [[invoiceTo, deliverTo]],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: {
        fillColor: [246, 248, 251],
        textColor: 30,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: addrColW },
        1: { cellWidth: innerW - addrColW },
      },
      margin: { left: margin, right: margin },
      tableLineColor: [225, 230, 235],
      tableLineWidth: 0.5,
      tableWidth: innerW,
      didDrawPage: () => pageFooter(doc),
    });

    y = doc.lastAutoTable.finalY + 12;

    // Items (single)
    const code = order.code || order.sku || "";
    const description = [`${order.product_name || ""}${order.size ? ` ${order.size}` : ""}`.trim(), `REF: ${getCompanyName(order)}`]
      .filter(Boolean)
      .join("\n");

    const codeW = Math.round(innerW * 0.18);
    const qtyW = Math.round(innerW * 0.14);
    const descW = innerW - codeW - qtyW;

    autoTable(doc, {
      startY: y,
      head: [["Code", "Description", "Qty/Hrs"]],
      body: [[code, description, String(order.quantity ?? "")]],
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 6,
        overflow: "linebreak",
        lineWidth: 0.5,
        lineColor: [225, 230, 235],
      },
      headStyles: {
        fillColor: [235, 242, 247],
        textColor: 30,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: codeW },
        1: { cellWidth: descW },
        2: { cellWidth: qtyW, halign: "right" },
      },
      margin: { left: margin, right: margin },
      tableWidth: innerW,
      didDrawPage: () => pageFooter(doc),
    });

    y = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(9);
    doc.text(`Customer Ref: ${order.referenceNumber || "-"}`, margin, y);

    // Signatures
    y += 24;
    const boxW = (innerW - 16) / 2;
    const boxH = 62;
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.8);

    doc.roundedRect(margin, y, boxW, boxH, 4, 4);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Customer / Received By", margin + 10, y + 16);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Name:", margin + 10, y + 34);
    doc.text("Signature:", margin + 10, y + 50);

    doc.roundedRect(margin + boxW + 16, y, boxW, boxH, 4, 4);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Driver / Dispatch", margin + boxW + 26, y + 16);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Name:", margin + boxW + 26, y + 34);
    doc.text("Signature:", margin + boxW + 26, y + 50);

    // Footer notes
    const notes = [
      "Thank you for choosing Serena Beds - Your premium bedding partner",
      "All goods remain the property of Serena Beds Limited until paid in full",
      "Please inspect goods upon delivery and report any issues immediately",
    ];
    const notesY = pageHeight - 48;
    doc.setFontSize(8);
    doc.setTextColor(100);
    notes.forEach((n, i) => doc.text(n, pageWidth / 2, notesY + i * 11, { align: "center" }));
    doc.setTextColor(0);
  };

  // ====== GROUPED (one customer; may span multiple pages) ======
  const drawCustomerDocket = (doc, ordersForCustomer) => {
    const first = ordersForCustomer[0];
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const innerW = pageWidth - margin * 2;

    const set = (size, style = "normal") => {
      doc.setFont("helvetica", style);
      doc.setFontSize(size);
    };
    const text = (t, x, y, opts) => doc.text(String(t ?? ""), x, y, opts);

    // Header
    let y = margin;
    const logoTopY = y - 6;
    const logoH = 55;
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, "PNG", margin, logoTopY, 110, logoH);
      } catch {}
    }

    const rightX = pageWidth - margin;
    let topY = y + 6;

    set(16, "bold");
    text("Serena Beds Limited", rightX, topY, { align: "right" });

    set(10, "normal");
    topY += 14;
    text("Unit 2, Ashbourne Business Centre", rightX, topY, { align: "right" });
    topY += 14;
    text("Ashbourne, Meath, A84 R960", rightX, topY, { align: "right" });
    topY += 14;
    text("Ireland", rightX, topY, { align: "right" });

    topY += 8;
    const lh = 12;
    topY += lh;
    text("www.serenabeds.ie", rightX, topY, { align: "right" });
    topY += lh;
    text("T: 08383855666", rightX, topY, { align: "right" });
    topY += lh;
    text("sales@serenabeds.ie", rightX, topY, { align: "right" });

    const headerBottom = Math.max(logoTopY + logoH, topY);

    y = headerBottom + 10;
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);

    // Title + meta
    y += 16;
    set(13, "bold");
    text("DELIVERY NOTE", rightX, y, { align: "right" });

    set(10, "normal");
    const metaY = y + 18;
    text("Date", margin, metaY);
    text(new Date(first.orderDate || first.order_date || Date.now()).toLocaleDateString(), margin + 55, metaY);

    set(9);
    text("Invoice Number", rightX, metaY, { align: "right" });
    set(10);
    const multiple =
      ordersForCustomer.length > 1
        ? "Multiple"
        : String(first.trackingId || first.orderId || first.id || "-");
    text(multiple, rightX, metaY + 14, { align: "right" });

    // Addresses
    y = metaY + 24;
    const invoiceTo =
      (first.billing_address || first.shipping_address || "").trim() || getCompanyName(first);
    const deliverTo =
      (first.shipping_address || "").trim() || getCompanyName(first);

    const addrColW = Math.floor(innerW / 2);
    autoTable(doc, {
      startY: y,
      head: [["Invoice To", "Deliver To"]],
      body: [[invoiceTo, deliverTo]],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: {
        fillColor: [246, 248, 251],
        textColor: 30,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: addrColW },
        1: { cellWidth: innerW - addrColW },
      },
      margin: { left: margin, right: margin },
      tableLineColor: [225, 230, 235],
      tableLineWidth: 0.5,
      tableWidth: innerW,
      didDrawPage: () => pageFooter(doc),
    });

    y = doc.lastAutoTable.finalY + 12;

    // Grouped items
    const rows = buildGroupedRows(ordersForCustomer);

    const catW = Math.round(innerW * 0.17);
    const sizeW = Math.round(innerW * 0.16);
    const codeW = Math.round(innerW * 0.16);
    const qtyW = Math.round(innerW * 0.11);
    const descW = innerW - catW - sizeW - codeW - qtyW;

    autoTable(doc, {
      startY: y,
      head: [["Category", "Size", "Code", "Description", "Qty"]],
      body: rows,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 6,
        overflow: "linebreak",
        lineWidth: 0.5,
        lineColor: [225, 230, 235],
      },
      headStyles: {
        fillColor: [235, 242, 247],
        textColor: 30,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: catW },
        1: { cellWidth: sizeW },
        2: { cellWidth: codeW },
        3: { cellWidth: descW },
        4: { cellWidth: qtyW, halign: "right" },
      },
      margin: { left: margin, right: margin },
      tableWidth: innerW,
      pageBreak: "auto",
      didDrawPage: () => pageFooter(doc),
    });

    // Signatures on last page
    const endPage = doc.internal.getNumberOfPages();
    doc.setPage(endPage);

    let yAfter = doc.lastAutoTable.finalY + 12;
    const boxW = (innerW - 16) / 2;
    const boxH = 62;
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.8);

    if (yAfter + boxH + 90 > pageHeight) {
      doc.addPage();
      yAfter = 40;
    }

    doc.roundedRect(margin, yAfter, boxW, boxH, 4, 4);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Customer / Received By", margin + 10, yAfter + 16);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Name:", margin + 10, yAfter + 34);
    doc.text("Signature:", margin + 10, yAfter + 50);

    doc.roundedRect(margin + boxW + 16, yAfter, boxW, boxH, 4, 4);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Driver / Dispatch", margin + boxW + 26, yAfter + 16);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Name:", margin + boxW + 26, yAfter + 34);
    doc.text("Signature:", margin + boxW + 26, yAfter + 50);

    const notes = [
      "Thank you for choosing Serena Beds - Your premium bedding partner",
      "All goods remain the property of Serena Beds Limited until paid in full",
      "Please inspect goods upon delivery and report any issues immediately",
    ];
    const notesY = pageHeight - 48;
    doc.setFontSize(8);
    doc.setTextColor(100);
    notes.forEach((n, i) => doc.text(n, pageWidth / 2, notesY + i * 11, { align: "center" }));
    doc.setTextColor(0);
  };

  // === PRINT grouped: one section per customer ===
  const handlePrintDockets = () => {
    // ✅ Sales should NOT export/print/email
    if (isSalesUser) return;

    if (!selectedDocketIds.length) return;
    const source = displayedOrders.filter((o) => selectedDocketIds.includes(o.id));
    if (!source.length) return;

    // group by customer
    const byCustomer = new Map();
    source.forEach((o) => {
      const c = getCompanyName(o) || "-";
      if (!byCustomer.has(c)) byCustomer.set(c, []);
      byCustomer.get(c).push(o);
    });

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "A4",
    });

    let pageIndex = 0;
    for (const [, list] of byCustomer) {
      if (pageIndex > 0) doc.addPage();
      if (list.length === 1) {
        drawDocket(doc, list[0]);
      } else {
        drawCustomerDocket(doc, list);
      }
      pageIndex++;
    }

    // ensure page numbers on all pages
    try {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        pageFooter(doc);
      }
    } catch {}

    doc.save("Dockets.pdf");
  };

  const handleEmailDockets = async () => {
    // ✅ Sales should NOT export/print/email
    if (isSalesUser) return;

    if (!selectedDocketIds.length) return;

    const source = displayedOrders.filter((o) => selectedDocketIds.includes(o.id));

    if (!source.length) return;

    // Group by customer
    const byCustomer = new Map();
    source.forEach((o) => {
      const c = getCompanyName(o) || "-";
      if (!byCustomer.has(c)) byCustomer.set(c, []);
      byCustomer.get(c).push(o);
    });

    for (const [customer, list] of byCustomer) {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "A4",
      });

      if (list.length === 1) {
        drawDocket(doc, list[0]);
      } else {
        drawCustomerDocket(doc, list);
      }

      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        pageFooter(doc);
      }

      const pdfBlob = doc.output("blob");

      const formData = new FormData();
      formData.append("pdf", pdfBlob, `Docket-${customer}.pdf`);
      formData.append("orderIds", JSON.stringify(list.map((o) => o.id)));

      await axiosInstance.post("/admin/order/email-dockets", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    message.success("Dockets emailed successfully.");
  };

  const canUploadForCustomer = () => {
    if (!selectedDocketIds.length) return false;

    const selectedOrders = displayedOrders.filter((o) => selectedDocketIds.includes(o.id));

    const uniqueCompanies = new Set(selectedOrders.map((o) => getCompanyName(o)));

    return uniqueCompanies.size === 1;
  };

  const anyDocketsSelected = selectedDocketIds.length > 0;

  // ✅ If sales, clear any selections (view-only)
  useEffect(() => {
    if (isSalesUser && selectedDocketIds.length) {
      setSelectedDocketIds([]);
    }
  }, [isSalesUser, selectedDocketIds.length]);

  const columns = [
    // ✅ Selection column ONLY for non-sales
    ...(!isSalesUser
      ? [
          {
            title: (
              <Checkbox
                checked={
                  displayedOrders.length > 0 &&
                  selectedDocketIds.length === displayedOrders.length
                }
                indeterminate={
                  selectedDocketIds.length > 0 &&
                  selectedDocketIds.length < displayedOrders.length
                }
                onChange={(e) => {
                  if (e.target.checked)
                    setSelectedDocketIds(displayedOrders.map((o) => o.id));
                  else setSelectedDocketIds([]);
                }}
              />
            ),
            dataIndex: "docket",
            key: "docket",
            width: 48,
            fixed: "left",
            render: (_, rec) => (
              <Checkbox
                checked={selectedDocketIds.includes(rec.id)}
                onChange={(e) => {
                  setSelectedDocketIds((prev) =>
                    e.target.checked
                      ? [...prev, rec.id]
                      : prev.filter((id) => id !== rec.id)
                  );
                }}
              />
            ),
          },
        ]
      : []),

    {
      title: "Sr.No",
      key: "srNo",
      width: 50,
      render: (_, __, idx) => (
        <div className="text-center font-medium text-gray-600">{idx + 1}</div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, rec) => {
        const companyName = getCompanyName(rec);
        const getInitials = (name) =>
          !name || name === "-"
            ? "C"
            : name
                .split(" ")
                .map((w) => w[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {getInitials(companyName)}
            </div>
            <div className="text-sm font-medium text-gray-900 leading-tight min-w-0 flex-1">
              {companyName}
            </div>
          </div>
        );
      },
    },
    {
      title: "Image",
      dataIndex: "product_image",
      key: "product_image",
      width: 80,
      render: (img) =>
        img ? (
          <AntImage
            src={img}
            width={50}
            height={50}
            fallback="https://via.placeholder.com/50"
            className="rounded"
          />
        ) : (
          <span className="text-gray-400">No Image</span>
        ),
    },
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      width: 120,
      render: splitProductName,
    },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 80,
      render: (d) => {
        const date = new Date(d);
        const val = isNaN(+date)
          ? String(d || "-")
          : date.toLocaleDateString(undefined, {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            });
        return (
          <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
            {val}
          </div>
        );
      },
    },
    {
      title: "Customer Ref",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 100,
      render: (val) => {
        const textVal = String(val ?? "-");
        const handleCopy = (e) => {
          if (e?.clipboardData) {
            e.clipboardData.setData("text/plain", textVal);
            e.preventDefault();
          }
        };
        return (
          <div
            className="font-mono text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap"
            title={textVal}
            onCopy={handleCopy}
          >
            <span className="block max-w-[10ch] truncate">{textVal}</span>
          </div>
        );
      },
    },
    {
      title: "Delivery Status",
      key: "deliveryStatus",
      width: 120,
      align: "center",
      render: (_, rec) => {
        const status = rec.deliveryStatus;
        const isDelivered = status === "order delivered";
        const isScheduled = status === "scheduled";
        const overdue = isOverdue(rec);

        const subText = isDelivered
          ? formatShortDate(rec.deliveredAt)
          : isScheduled
          ? formatShortDate(rec.scheduleDate)
          : null;

        const displayStatus = isDelivered ? "delivered" : status;
        return (
          <div className="leading-tight text-center">
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                isDelivered
                  ? "bg-green-100 text-green-800"
                  : overdue
                  ? "bg-red-100 text-red-700"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {displayStatus}
            </span>
            {subText && (
              <div
                className={`mt-1 text-[11px] font-medium ${
                  overdue ? "text-red-600" : "text-gray-700"
                }`}
              >
                {subText}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 190,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Documents">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleViewDocument(record)}
              className="bg-blue-500 hover:bg-blue-600 border-blue-500"
              size="small"
            />
          </Tooltip>

          {/* ✅ Sales = view-only (no upload / no mark delivered) */}
          {!isSalesUser && (
            <>
              <Tooltip title="Upload Document">
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setUploadModalOrder(record);
                    setUploadModalVisible(true);
                  }}
                  className="bg-green-500 hover:bg-green-600 border-green-500"
                  size="small"
                />
              </Tooltip>
              <Tooltip
                title={
                  record.deliveryStatus === "order delivered"
                    ? "Already Delivered"
                    : "Mark as Delivered"
                }
              >
                <Button
                  type={record.deliveryStatus === "order delivered" ? "default" : "primary"}
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleMarkAsDelivered(record)}
                  disabled={record.deliveryStatus === "order delivered"}
                  className={
                    record.deliveryStatus === "order delivered"
                      ? "opacity-50 cursor-not-allowed"
                      : "bg-purple-500 hover:bg-purple-600 border-purple-500"
                  }
                  size="small"
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Signed Delivery Notes
                </h1>
                <p className="text-gray-600">
                  Manage delivery documents and confirmations
                </p>
              </div>

              {/* ✅ For Sales role: hide print/export/email/upload docket buttons */}
              {!isSalesUser && (
                <Space wrap>
                  <Button
                    icon={<PrinterOutlined />}
                    type="primary"
                    onClick={handleExportPDF}
                    className="bg-red-500 hover:bg-red-600 border-red-500 text-white font-medium"
                  >
                    {anyDocketsSelected ? "Print Selected (PDF)" : "Print All (PDF)"}
                  </Button>

                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExportExcel}
                    className="bg-green-500 hover:bg-green-600 border-green-500 text-white font-medium"
                  >
                    {anyDocketsSelected ? "Export Selected" : "Export All"}
                  </Button>

                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handlePrintDockets}
                    disabled={!anyDocketsSelected}
                    className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Print Dockets
                  </Button>

                  <Button
                    icon={<MailOutlined />}
                    onClick={handleEmailDockets}
                    disabled={!anyDocketsSelected}
                    className="bg-purple-500 hover:bg-purple-600 border-purple-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Email Dockets
                  </Button>

                  <Button
                    icon={<UploadOutlined />}
                    type="dashed"
                    disabled={!canUploadForCustomer()}
                    onClick={openBulkUploadForCurrentCustomer}
                    className="bg-orange-500 hover:bg-orange-600 border-orange-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Upload Docket
                  </Button>
                </Space>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Order ID (Tracking ID)
                </label>
                <Input
                  placeholder="e.g., SER-2025-537"
                  value={trackingFilter}
                  onChange={(e) => setTrackingFilter(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Customer Ref
                </label>
                <Input
                  placeholder="Search by Customer Ref…"
                  value={referenceFilter}
                  onChange={(e) => setReferenceFilter(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <UserOutlined className="mr-2" />
                  Filter by Customer
                </label>
                <Select
                  placeholder="Select customer..."
                  value={customerFilter || undefined}
                  onChange={setCustomerFilter}
                  allowClear
                  className="w-full h-10 rounded-lg"
                  optionFilterProp="label"
                  showSearch
                >
                  {customerOptions.map(({ name, count }) => (
                    <Option key={name} value={name} label={name}>
                      <div className="flex justify-between items-center">
                        <span>{name}</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {count}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Order Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {displayedOrders.length} orders
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={displayedOrders}
              columns={columns}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                className: "px-6 py-4",
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              scroll={{ x: "max-content" }}
              className="modern-orders-table"
              rowClassName={(record) => {
                const base = "hover:bg-gray-50 transition-colors duration-200";
                if (record.deliveryStatus === "order delivered") {
                  return `${base} bg-green-50 border-l-4 border-l-green-400`;
                }
                if (isOverdue(record)) {
                  return `${base} bg-red-50 border-l-4 border-l-red-400`;
                }
                return base;
              }}
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">
                      No delivery notes found
                    </div>
                    <div className="text-sm text-gray-400">
                      Try adjusting your search or filters
                    </div>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* View Document Modal */}
      <Modal
        open={viewModalVisible}
        title={
          <div className="flex items-center space-x-2">
            <EyeOutlined className="text-blue-500" />
            <span>View Documents</span>
          </div>
        }
        footer={null}
        onCancel={() => setViewModalVisible(false)}
        width={800}
        className="modern-modal"
      >
        <List
          grid={{ gutter: 16, column: 3 }}
          dataSource={viewDocuments}
          renderItem={(doc) => (
            <List.Item>
              <div className="text-center">
                {isPDF(doc.image_url) ? (
                  <div
                    className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-blue-300 transition-colors"
                    onClick={() => handleOpenDocument(doc.image_url)}
                  >
                    <FilePdfOutlined className="text-4xl text-blue-500" />
                    <p className="mt-2 text-sm text-gray-600">
                      {doc.image_url.split("/").pop()}
                    </p>
                  </div>
                ) : (
                  <div
                    className="cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => handleOpenDocument(doc.image_url)}
                  >
                    <AntImage
                      src={doc.image_url}
                      alt="Uploaded Document"
                      className="w-full rounded-lg border border-gray-200"
                      preview={false}
                    />
                  </div>
                )}
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                  {doc.comment || "No comment provided"}
                </div>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      {/* Upload Document Modal (single order) */}
      <Modal
        open={uploadModalVisible}
        title={
          <div className="flex items-center space-x-2">
            <UploadOutlined className="text-green-500" />
            <span>Upload Document</span>
          </div>
        }
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedFile(null);
        }}
        onOk={handleUploadSubmit}
        okText="Upload"
        className="modern-modal"
      >
        <Form layout="vertical">
          <Form.Item label="Select Document" required>
            <Upload
              beforeUpload={(file) => {
                setSelectedFile(file);
                return false;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} className="rounded-lg">
                Choose File
              </Button>
            </Upload>
            {renderFilePreview(selectedFile)}
          </Form.Item>
          <Form.Item label="Comment (Optional)">
            <Input.TextArea
              value={uploadComment}
              onChange={(e) => setUploadComment(e.target.value)}
              placeholder="Add a comment about this document..."
              rows={3}
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ✅ Bulk Upload Docket Modal (per customer, multiple orders) */}
      <Modal
        open={bulkUploadVisible}
        title={
          <div className="flex items-center space-x-2">
            <UploadOutlined className="text-green-500" />
            <span>Upload Docket for This Customer</span>
          </div>
        }
        onCancel={() => {
          setBulkUploadVisible(false);
          setBulkSelectedFile(null);
          setBulkSelectedOrders([]);
        }}
        onOk={handleBulkUploadSubmit}
        okText={bulkUploading ? "Uploading..." : "Upload for Selected Orders"}
        confirmLoading={bulkUploading}
        className="modern-modal"
        width={700}
      >
        <Form layout="vertical">
          <Form.Item label="Customer">
            <Input value={bulkUploadCustomer || ""} disabled prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item
            label="Orders for this customer (select which orders this docket covers)"
            required
          >
            <div className="max-h-56 overflow-auto border border-gray-200 rounded-md p-2">
              <Checkbox.Group
                style={{ width: "100%" }}
                value={bulkSelectedOrders}
                onChange={(vals) => setBulkSelectedOrders(vals)}
              >
                <List
                  dataSource={bulkCustomerOrders}
                  renderItem={(order) => (
                    <List.Item key={order.id} style={{ padding: "4px 0" }}>
                      <Checkbox value={order.id} style={{ width: "100%" }}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm">
                          <div>
                            <span className="font-medium">
                              {order.trackingId || order.orderId || order.id}
                            </span>
                            {order.referenceNumber && (
                              <span className="text-gray-500 ml-2">
                                • Ref: {order.referenceNumber}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 sm:mt-0">
                            {normalizeSize(order.size)} • {formatShortDate(order.orderDate)}
                          </div>
                        </div>
                      </Checkbox>
                    </List.Item>
                  )}
                />
              </Checkbox.Group>
            </div>
          </Form.Item>

          <Form.Item label="Select Docket File" required>
            <Upload
              beforeUpload={(file) => {
                setBulkSelectedFile(file);
                return false;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} className="rounded-lg">
                Choose File
              </Button>
            </Upload>
            {renderFilePreview(bulkSelectedFile)}
          </Form.Item>

          <Form.Item label="Comment (Optional)">
            <Input.TextArea
              value={bulkUploadComment}
              onChange={(e) => setBulkUploadComment(e.target.value)}
              placeholder="Add a comment about this docket..."
              rows={3}
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }

        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }

        /* ✅ ENSURE GREEN BACKGROUND COVERS FULL ROW */
        .modern-orders-table .ant-table-tbody > tr.bg-green-50 > td {
          background-color: rgb(240 253 244) !important; /* green-50 */
          border-bottom-color: rgb(220 252 231) !important; /* green-100 */
        }

        .modern-orders-table .ant-table-tbody > tr.bg-green-50:hover > td {
          background-color: rgb(220 252 231) !important; /* green-100 on hover */
        }

        .modern-orders-table .ant-table-tbody > tr.bg-red-50 > td {
          background-color: rgb(254 242 242) !important; /* red-50 */
          border-bottom-color: rgb(254 202 202) !important; /* red-100 */
        }

        .modern-orders-table .ant-table-tbody > tr.bg-red-50:hover > td {
          background-color: rgb(254 202 202) !important; /* red-100 on hover */
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
      `}</style>
    </Spin>
  );
};

export default OrderSignedDeliveryNote;